import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import BusinessCustomersActions from "../Redux/transition/customers/business-customer.reducer";
import PaymentTrackActions from "../Redux/transition/payment-track/payment-track.reducer";
import { useNavigate } from 'react-router-dom';
import { CustomerSearchPicker } from "./CustomerSearchField";
import "../Styles/components/tags.scss";


export const GlobalSearch = (props) => {
    const { defMembership, globalBorderWidth, globalQuery, state, setState, onCustomerSelect, skipDetailsFetch, compact, placeholder } = props;
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const searchPendingRef = useRef(false);
    const [resultsOpen, setResultsOpen] = useState(false);
    const [awaitingResults, setAwaitingResults] = useState(false);

    //redux states
    const { exactFilteredBusinessCustomers, businessCustomerRecord, fetchingExactFilteredBusinessCustomers } = useSelector((state) => state.businessCustomers);

    //redux calls
    const dispatch = useDispatch();
    const getExactFilteredBusinessCustomers = useCallback((businessId, query, options) => dispatch(BusinessCustomersActions.exactFilteredBusinessCustomerRequest(businessId, query, options)), [dispatch])
    const getEnterpriseCustomers = useCallback((businessId) => dispatch(BusinessCustomersActions.businessCustomerRecordRequest(businessId)), [dispatch]);
    const getBusinessCustomerSubscriptions = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.businessCustomerSubscriptionsRequest(businessCustomerId)), [dispatch]);
    const getBusinessCustomerRecord = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.businessCustomerRecordRequest(businessCustomerId)), [dispatch]);
    const getRecentCustomerPayments = useCallback((businessCustomerId, size) => dispatch(BusinessCustomersActions.businessCustomerPaymentsRequest(businessCustomerId, size)), [dispatch]);
    const getRecentMissedCustomerPayments = useCallback((businessCustomerId, size) => dispatch(BusinessCustomersActions.businessCustomerMissedPaymentsRequest(businessCustomerId, size)), [dispatch]);
    const getRecentSubscriptionActivity = useCallback((businessCustomerId, size) => dispatch(BusinessCustomersActions.businessCustomerSubscriptionActivityRequest(businessCustomerId, size)), [dispatch]);
    const getCustomerPendingPayments = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.customerPendingPaymentRequest(businessCustomerId)), [dispatch]);
    const getCustomerMissedPayments = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.customerMissedPaymentRequest(businessCustomerId)), [dispatch]);
    const getCustomerReceivedPayments = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.customerReceivedPaymentRequest(businessCustomerId)), [dispatch]);
    const getSuccessfulPayments = useCallback((bcId) => { dispatch(BusinessCustomersActions.successfulPaymentsRequest(bcId)) }, [dispatch])
    const getExpiredPayments = useCallback((bcId) => { dispatch(BusinessCustomersActions.expiredPaymentsRequest(bcId)) }, [dispatch])
    const getBusinessCustomerExternalPayments = useCallback((businessId, bcId) => { dispatch(PaymentTrackActions.businessCustomerExternalPaymentsRequest(businessId, bcId)) }, [dispatch])

    const searchValue = typeof state.query === "string" || state.query == null
        ? (state.query || "")
        : (state.query?.name || "");

    const runSearch = () => {
        const query = searchValue.trim();
        if (query.length < 3 || !defMembership?.business?.id) {
            return;
        }
        searchPendingRef.current = true;
        setAwaitingResults(true);
        setResultsOpen(true);
        setState((prev) => ({ ...prev, exactFilteredBusinessCustomers: [] }));
        getExactFilteredBusinessCustomers(defMembership.business.id, query);
    };

    const closeResults = () => {
        searchPendingRef.current = false;
        setAwaitingResults(false);
        setResultsOpen(false);
    };

    const onClickCancelBtn = () => {
        closeResults();
        setState((prev) => ({
            ...prev,
            filteredCustomers: false,
            query: "",
            selectedCustomerId: null,
            isCustomerSelected: false,
            exactFilteredBusinessCustomers: [],
        }));
        onCustomerSelect?.(null);
    };

    useEffect(() => {
        if (!searchPendingRef.current || fetchingExactFilteredBusinessCustomers) return;
        searchPendingRef.current = false;
        setAwaitingResults(false);
        setState((prev) => ({
            ...prev,
            exactFilteredBusinessCustomers: exactFilteredBusinessCustomers || [],
        }));
    }, [fetchingExactFilteredBusinessCustomers, exactFilteredBusinessCustomers, setState]);

    useEffect(() => {
        if (businessCustomerRecord && !skipDetailsFetch) {
            setState((prev) => ({ ...prev, businessCustomerRecord }));
        }
        if (state.isCustomerSelected && !skipDetailsFetch) {
            const customerId = state.selectedCustomerId
            if (globalQuery) {
                navigate(`/crm/customers/customer/${customerId}`)
            }
            getEnterpriseCustomers(state.selectedCustomerId)
            getBusinessCustomerSubscriptions(state.selectedCustomerId)
            getBusinessCustomerRecord(state.selectedCustomerId)
            getRecentCustomerPayments(state.selectedCustomerId, "3")
            getRecentMissedCustomerPayments(state.selectedCustomerId, "3")
            getRecentSubscriptionActivity(state.selectedCustomerId, "3")
            getCustomerPendingPayments(state.selectedCustomerId)
            getCustomerMissedPayments(state.selectedCustomerId)
            getCustomerReceivedPayments(state.selectedCustomerId)
            getSuccessfulPayments(state.selectedCustomerId)
            getExpiredPayments(state.selectedCustomerId)
            getBusinessCustomerExternalPayments(defMembership.business.id, state.selectedCustomerId)
            setState((prev) => ({ ...prev, isCustomerSelected: false }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isCustomerSelected, state.selectedCustomerId, globalQuery, businessCustomerRecord]);

    // Signatory / other dialogs can leave autofilled phone values in this field — clear on request.
    useEffect(() => {
        const clearQuery = () => {
            closeResults();
            setState((prev) => ({
                ...prev,
                filteredCustomers: false,
                query: "",
                exactFilteredBusinessCustomers: [],
            }));
        };
        window.addEventListener("reveknew:clear-global-search", clearQuery);
        return () => window.removeEventListener("reveknew:clear-global-search", clearQuery);
    }, [setState]);

    const fieldStyle = {};
    if (globalBorderWidth?.minWidth) fieldStyle.minWidth = globalBorderWidth.minWidth;
    if (globalBorderWidth?.width) fieldStyle.width = globalBorderWidth.width;

    return (
        <CustomerSearchPicker
            id="global_search"
            value={searchValue}
            placeholder={placeholder || t("common.global_search")}
            suggestions={state.exactFilteredBusinessCustomers}
            open={resultsOpen}
            loading={awaitingResults && resultsOpen}
            compact={compact}
            emptyLabel={t("common.no_data")}
            style={fieldStyle}
            onChange={(next) => {
                closeResults();
                setState((prev) => ({ ...prev, query: next }));
            }}
            onSearch={runSearch}
            onClear={onClickCancelBtn}
            onClose={closeResults}
            onSelect={(customer) => {
                closeResults();
                setState((prev) => ({
                    ...prev,
                    selectedCustomerId: customer?.id,
                    isCustomerSelected: !skipDetailsFetch,
                    query: customer?.name || "",
                }));
                onCustomerSelect?.(customer || null);
            }}
        />
    )
}
